// packages/api/src/public/magic-link-session.ts
//
// Fonte única de storage da sessão de identidade do magic-link (/p/:token).
// Consumida pelo interceptor do publicApiClient, pelo hook
// useVerifyMagicLinkIdentity e pelo MagicLinkGate (apps/web).
//
// sessionStorage (não localStorage) — a sessão vale só enquanto a aba viver.
// Chave por token do link: `alamo:mlsession:{token}`. Guarda MagicLinkSession
// ({ sessionToken, expiresIn, expiresAt }); o TTL é aplicado na leitura via
// `expiresAt` (não há precedente de TTL em storage no projeto — estabelecido
// aqui). Contract: docs/contracts/ESCR-magic-link-auth.md.
//
// NÃO tocar `alamo_refresh_token` (localStorage, client.ts) — domínio separado.

import type { MagicLinkSession } from '@alamo/types';

const KEY_PREFIX = 'alamo:mlsession:';

function storageKey(token: string): string {
  return `${KEY_PREFIX}${token}`;
}

/**
 * Lê a sessão do token. Retorna null se ausente, corrompida OU expirada —
 * expirada = `Date.parse(expiresAt)` menor ou igual a `Date.now()` (epoch ms).
 * Um `expiresAt` não-parseável também retorna null (trata como inválida).
 */
export function getMagicLinkSession(token: string): MagicLinkSession | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MagicLinkSession>;
    const { sessionToken, expiresAt, expiresIn } = parsed;
    if (typeof sessionToken !== 'string' || typeof expiresAt !== 'string') {
      return null;
    }
    const expiresAtMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      return null;
    }
    return {
      sessionToken,
      expiresAt,
      expiresIn: typeof expiresIn === 'number' ? expiresIn : 0,
    };
  } catch {
    return null;
  }
}

/** Persiste a sessão emitida pelo verify-identity. */
export function setMagicLinkSession(token: string, session: MagicLinkSession): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(storageKey(token), JSON.stringify(session));
  } catch {
    // sessionStorage indisponível (SSR / modo privacidade / cota).
  }
}

/** Remove a sessão do token (sessão inválida/expirada, ou logout do link). */
export function clearMagicLinkSession(token: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(storageKey(token));
  } catch {
    // sessionStorage indisponível.
  }
}

/** Remove todas as sessões de Magic Link da aba ao assumir uma sessão do CRM. */
export function clearAllMagicLinkSessions(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage indisponível.
  }
}

/** Bearer válido do token para o interceptor, ou null se sem sessão/expirada. */
export function getMagicLinkBearer(token: string): string | null {
  const session = getMagicLinkSession(token);
  return session ? session.sessionToken : null;
}
